import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import {
  Phone,
  Mail,
  MessageSquare,
  ArrowRight,
  AlertTriangle,
  ArrowLeft,
  Check,
  Info,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import SurveyService from "../lib/SurveyService";
import preferencesImage from "../assets/images/preferences.svg";
import analyticsService from "../lib/AnalyticsService";
import { useInteractionTracking } from "../hooks/useAnalytics";
import API_CONFIG from "../lib/ApiConfig";

const ContactPermissionScreen = ({
  buyerData,
  userData,
  onComplete,
  onBack,
}) => {
  // We'll determine read-only state internally based on previous responses
  const [readOnly, setReadOnly] = useState(false);
  const { user, getIdToken } = useAuth();
  const { trackButtonClick } = useInteractionTracking();

  // Survey ID for contact preferences
  const SURVEY_ID = "STEM_D8xqtv43XX";

  // Contact preference states using checkboxes
  const [contactMethods, setContactMethods] = useState({
    call_8am_8pm: false,
    email: false,
    text: false,
  });

  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [previousResponseFound, setPreviousResponseFound] = useState(false);

  useEffect(() => {
    analyticsService.trackPageView("simplyioa_contact_preferences", {});
  }, []);

  // Effect to fetch and load previous survey responses
  useEffect(() => {
    const fetchPreviousResponses = async () => {
      try {
        setIsLoading(true);

        // First try to get from the server API
        const token = await getIdToken();
        if (token) {
          try {
            // Fetch previous responses from the API
            const responseData = await SurveyService.getSurveyResponsesBySurveyId(
              SURVEY_ID,
              token
            );

            if (
              responseData &&
              responseData.responses &&
              responseData.responses.length > 0
            ) {
              // Store in session storage for future loads
              sessionStorage.setItem(
                "contactPreferencesData",
                JSON.stringify(responseData)
              );

              applyPreviousSelections(responseData.responses);
              // Set these flags when responses come directly from the server
              setPreviousResponseFound(true);
              setReadOnly(true);
              setIsLoading(false);
              return; // Exit early since we got server data
            }
          } catch (apiErr) {
            console.log("Error fetching from API, will try cache:", apiErr);
            // Continue to cache check if API fails
          }
        }

        // Fall back to session storage if server check fails or returns no data
        const storedData = sessionStorage.getItem("contactPreferencesData");
        if (storedData) {
          const parsedData = JSON.parse(storedData);

          if (
            parsedData &&
            parsedData.responses &&
            parsedData.responses.length > 0
          ) {
            applyPreviousSelections(parsedData.responses);
            // Do NOT set previousResponseFound or readOnly when loading from cache
          }
        }
      } catch (err) {
        console.error("Error fetching previous contact preferences:", err);
        // Don't set error - just proceed with empty form
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreviousResponses();
  }, [getIdToken]);

  // Helper function to process previous selections and update state
  const applyPreviousSelections = (responses) => {
    // Find the contact methods question response
    const contactQuestion = responses.find(
      (r) => r.questionId === "SQUE_9r6CPcbGcG"
    );

    if (contactQuestion && contactQuestion.selections) {
      const newContactMethods = { ...contactMethods };

      // Parse the selections and update the state
      contactQuestion.selections.forEach((selection) => {
        if (
          selection.optionValue === "call_8am_8pm" ||
          selection.value === "call_8am_8pm"
        ) {
          newContactMethods.call_8am_8pm = true;
        }
        if (selection.optionValue === "email" || selection.value === "email") {
          newContactMethods.email = true;
        }
        if (selection.optionValue === "text" || selection.value === "text") {
          newContactMethods.text = true;
        }
      });

      setContactMethods(newContactMethods);
    }
  };

  const handleCheckboxChange = (method) => {
    // If readonly, don't allow changes
    if (readOnly) return;

    setContactMethods((prev) => ({
      ...prev,
      [method]: !prev[method],
    }));
    setError(null);
  };

  // Helper method to check if user has existing reward
  const checkExistingReward = async (token) => {
    try {
      const rewardsUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/rewards`;
      const response = await fetch(rewardsUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
      });

      if (response.ok) {
        const rewards = await response.json();
        return (
          rewards &&
          rewards.length > 0 &&
          rewards.some((reward) => reward.buyer?.id === buyerData?.dataBuyerId)
        );
      }
      return false;
    } catch (err) {
      console.error("Error checking existing reward:", err);
      return false;
    }
  };

  // Helper method to accept offer
  const acceptOffer = async (token) => {
    try {
      const acceptOfferUrl = `${API_CONFIG.BASE_URL}/api/users/insurance-profile/offers/${buyerData.id}/accept`;
      const response = await fetch(acceptOfferUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
        body: JSON.stringify({
          dataBuyerId: buyerData.dataBuyerId,
          amount: buyerData.offer.amount,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to accept offer");
      }

      console.log("Offer accepted successfully");
      return true;
    } catch (err) {
      console.error("Error accepting offer:", err);
      throw err;
    }
  };

  const validateAndContinue = async () => {
    // If in readonly mode, just proceed to the next screen with existing data
    setIsSubmitting(true);
    try {
      if (readOnly) {
        const token = await getIdToken();
        const hasReward = await checkExistingReward(token);
        if (!hasReward && buyerData?.id) {
          // User needs their reward
          await acceptOffer(token);
        }
        // Get the previous responses from session storage
        const storedData = sessionStorage.getItem("contactPreferencesData");
        let previousResponses = [];

        if (storedData) {
          const parsedData = JSON.parse(storedData);
          if (parsedData.responses) {
            previousResponses = parsedData.responses;
          }
        }

        onComplete({
          contactMethods,
          readOnly: true,
          previouslySubmitted: true,
          responses: previousResponses,
        });
        return;
      }

      // For new submissions (should not happen given our auto read-only approach)
      // Adding this as a safeguard in case the component is used differently in the future

      // Check if at least one contact method is selected
      if (
        !contactMethods.call_8am_8pm &&
        !contactMethods.email &&
        !contactMethods.text
      ) {
        setError("Please select at least one contact method");
        return;
      }

      try {
        setIsSubmitting(true);

        // Prepare selected options for survey submission
        const selectedOptions = [];
        if (contactMethods.call_8am_8pm) {
          selectedOptions.push({
            id: "COPT_IDUb8Rejlv",
            value: "call_8am_8pm",
          });
        }
        if (contactMethods.email) {
          selectedOptions.push({
            id: "COPT_OBVSTCGcEA",
            value: "email",
          });
        }
        if (contactMethods.text) {
          selectedOptions.push({
            id: "COPT_ZrKbIMjB34",
            value: "text",
          });
        }

        // Format survey response data
        const surveyResponse = [
          {
            questionId: "SQUE_9r6CPcbGcG",
            type: "CHOICE",
            selections: selectedOptions,
          },
        ];

        // Get authentication token
        const token = await getIdToken();

        // Submit survey responses using the correct method from SurveyService
        if (token && user) {
          try {
            // Use the standard submitSurveyResponses method
            const responseData = await SurveyService.submitInsuranceProfileSurvey(
              SURVEY_ID,
              "",
              surveyResponse,
              token
            );

            console.log("Contact preferences submitted:", responseData);

            // Store in session storage for future loads
            sessionStorage.setItem(
              "contactPreferencesData",
              JSON.stringify({
                responses: surveyResponse,
                dateCompleted: new Date().toISOString(),
              })
            );

            // Set to read-only now that we've submitted
            setReadOnly(true);
            if (buyerData && buyerData.id) {
              let offerAccepted = false;
              const hasReward = await checkExistingReward(token);
              if (!hasReward) {
                await acceptOffer(token);
                offerAccepted = true;
              }
              setReadOnly(true);
              onComplete({
                contactMethods,
                responses: surveyResponse,
                offerAccepted,
              });
            } else {
              // No buyer data, just proceed with survey completion
              setReadOnly(true);
              onComplete({
                contactMethods,
                responses: surveyResponse,
              });
            }
          } catch (submitErr) {
            console.error("Error submitting survey:", submitErr);
            throw submitErr;
          }
        }

        // Proceed to the next step with the survey response data
        onComplete({
          contactMethods,
          responses: surveyResponse,
        });
      } catch (err) {
        console.error("Error submitting contact preferences:", err);
        setError("Failed to save your contact preferences. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error("Error in validateAndContinue:", err);
      setError(err.message || "An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading spinner while fetching previous responses
  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
        <span className="ml-3">Loading your preferences...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      {/* {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )} */}

      {/* Header */}

      {/* Previously submitted notification if applicable */}
      {previousResponseFound && (
        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="pt-6 pb-6">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-700">
                  You've previously set your contact preferences. These settings
                  cannot be changed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact Method Selection */}
      <Card className="rounded-[20px]">
        <CardContent className="p-5 space-y-4">
          <div className="text-center space-y-4">
            <h1 className="text-xl font-bold uppercase">
              {previousResponseFound && readOnly
                ? "Your Contact Preferences"
                : "Set Your Preferences"}
            </h1>
            <img
              className="mx-auto !my-6"
              src={preferencesImage}
              alt="Preferences"
            />
            <p className="text-lg">
              {previousResponseFound && readOnly ? (
                `These are the contact methods you've allowed for ${
                  buyerData?.name || "our partner"
                }.`
              ) : (
                <>
                  {"Choose how "}
                  <span style={{ color: "#7A5CFA", fontWeight: 700 }}>
                    {buyerData?.name}
                  </span>
                  {
                    " can contact you to get started. You must select at least one."
                  }
                </>
              )}
            </p>
          </div>
          {/* Phone Call Option */}
          <label
            className={`flex items-center gap-3 !mt-6 transition-colors ${
              readOnly ? "cursor-default" : "cursor-pointer"
            } ${
              contactMethods.call_8am_8pm
                ? "border-[#7A5CFA] bg-white"
                : "border-gray-200 hover:border-[#7A5CFA]"
            }`}
          >
            <input
              type="checkbox"
              checked={contactMethods.call_8am_8pm}
              onChange={() => handleCheckboxChange("call_8am_8pm")}
              className="h-5 w-5 accent-[#7A5CFA] border-2 border-[#7A5CFA] rounded-md focus:ring-0 focus:outline-none"
              disabled={readOnly}
            />
            <div className="flex flex-1 items-center">
              <p className="text-darkGrey">
                I’d like a call during office hours
                <br />
                (8am–8pm EST)
              </p>
            </div>
          </label>

          {/* Email Option */}
          <label
            className={`flex items-center gap-3 !mt-6 transition-colors ${
              readOnly ? "cursor-default" : "cursor-pointer"
            } ${
              contactMethods.email
                ? "border-[#7A5CFA] bg-white"
                : "border-gray-200 hover:border-[#7A5CFA]"
            }`}
          >
            <input
              type="checkbox"
              checked={contactMethods.email}
              onChange={() => handleCheckboxChange("email")}
              className="h-5 w-5 accent-[#7A5CFA] border-2 border-[#7A5CFA] rounded-md focus:ring-0 focus:outline-none"
              disabled={readOnly}
            />
            <div className="flex flex-1 items-center">
              <p className="text-darkGrey">Please email me more info</p>
            </div>
          </label>

          {/* SMS Option */}
          <label
            className={`flex items-center gap-3 !mt-6 transition-colors ${
              readOnly ? "cursor-default" : "cursor-pointer"
            } ${
              contactMethods.text
                ? "border-[#7A5CFA] bg-white"
                : "border-gray-200 hover:border-[#7A5CFA]"
            }`}
          >
            <input
              type="checkbox"
              checked={contactMethods.text}
              onChange={() => handleCheckboxChange("text")}
              className="h-5 w-5 accent-[#7A5CFA] border-2 border-[#7A5CFA] rounded-md focus:ring-0 focus:outline-none"
              disabled={readOnly}
            />
            <div className="flex flex-1 items-center">
              <p className="text-darkGrey">Text me to get started</p>
            </div>
          </label>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 p-3 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-red-700 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Navigation Buttons */}
      <button
        onClick={() => {
          trackButtonClick("save-contact-preferences", {
            step_name: "simplyioa_contact_preferences",
          });
          validateAndContinue();
        }}
        disabled={
          isSubmitting ||
          (!contactMethods.call_8am_8pm &&
            !contactMethods.email &&
            !contactMethods.text)
        }
        className={`w-full flex items-center justify-center gap-2 uppercase font-bold px-4 py-3 rounded-[10px] transition-colors ${
          isSubmitting ||
          (!contactMethods.call_8am_8pm &&
            !contactMethods.email &&
            !contactMethods.text)
            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
            : "bg-purpleGradient text-white hover:bg-purpleGradientDark"
        }`}
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Saving preferences...</span>
          </>
        ) : (
          <>
            <span>Let’s make the connection</span>
            {/* <ArrowRight className="w-4 h-4" /> */}
          </>
        )}
      </button>
      {/* Legal Text */}
      <p className="text-darkGrey mt-2 px-1" style={{ fontSize: "10px" }}>
        By clicking on "
        <span style={{ color: "#7A5CFA" }}>Let’s Make The Connection</span>",
        you accept that the information provided will be used to accurately
        quote your policy, and that SimplyIOA will obtain and share some
        personal information with consumer reporting agencies and other third
        parties to complete your quote. You also understand that SimplyIOA may
        send additional communications regarding SimplyIOA services.
        <br />
        <br />
        By proceeding, SimplyIOA may view your Ownli Insurance Profile and the
        information shared to help you obtain the most accurate quote. For more
        information about SimplyIOA’s privacy practices,{" "}
        <a
          href="https://www.simplyioa.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#7A5CFA", textDecoration: "underline" }}
        >
          click here
        </a>
        .
      </p>
    </div>
  );
};

export default ContactPermissionScreen;
